import { Card, Group, Text } from '@mantine/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '../../../../components/Image';
import ErrorIcon from '../../../../assets/images/icons/icon_error.svg';
import WarningIcon from '../../../../assets/images/icons/warning.svg';
import InfoIcon from '../../../../assets/images/icons/icon_info.svg';
import CheckIcon from '../../../../assets/images/icons/check.svg';
import { formattedCentsToDollars } from '../../../../lib/util';

export function StatusSection(props: {
  hasDevice: boolean;
  hasSubscription: boolean;
  canManageSubscription: boolean;
  billingEmail: string;
  willPause: boolean;
  isCurrentlyPaused: boolean;
  isCanceled: boolean;
  isCanceledFreeTrial: boolean;
  isExpired: boolean;
  expirationDateString: string;
  petName: string;
  isDiscounted: boolean;
  renewalPriceString: string;
  renewalDateString: string;
  pendingCxResponse: boolean;
  pendingChange?: { priceInCents: number; term: string };
  lastPaymentFailed: boolean;
}) {
  const {
    hasDevice,
    hasSubscription,
    canManageSubscription,
    billingEmail,
    willPause,
    isCurrentlyPaused,
    isCanceled,
    isCanceledFreeTrial,
    isExpired,
    expirationDateString,
    petName,
    isDiscounted,
    renewalPriceString,
    renewalDateString,
    pendingCxResponse,
    pendingChange,
    lastPaymentFailed,
  } = props;
  const { t } = useTranslation();

  if (!hasDevice) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FEE6E6">
        <Group noWrap>
          <Image image={{ type: 'image', url: ErrorIcon }} />
          <Text fz={14} fw={600}>
            {t('please_reach_out_to_customer_experience_to_manage_pet_name_membership', { petName })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (!hasSubscription) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FEE6E6">
        <Group noWrap>
          <Image image={{ type: 'image', url: ErrorIcon }} />
          <Text fz={14} fw={600}>
            {t('there_is_no_active_membership_associated_with_pet_name_device', { petName })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (isExpired) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FEE6E6">
        <Group noWrap>
          <Image image={{ type: 'image', url: ErrorIcon }} />
          <Text fz={14} fw={600}>
            {t('membership_expired_date', { date: expirationDateString })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (!canManageSubscription) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FFEAA1">
        <Group noWrap>
          <Image image={{ type: 'image', url: WarningIcon }} />{' '}
          <Text fz={14} fw={600}>
            {t('only_billing_email_can_manage_membership_for_this_account', { billingEmail })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (lastPaymentFailed) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FEE6E6">
        <Group noWrap>
          <Image image={{ type: 'image', url: ErrorIcon }} />
          <Text fz={14} fw={600}>
            {t('your_payment_has_failed_please_update_your_payment_method_to_keep_your_device_online')}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (pendingCxResponse) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#F3F3F2">
        <Group noWrap>
          <Image image={{ type: 'image', url: InfoIcon }} />
          <Text fz={14} fw={600}>
            {t('a_specialist_is_investigating_your_issue_and_will_reach_out_shortly')}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (isCanceledFreeTrial && isCanceled) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FFEAA1">
        <Group noWrap>
          <Image image={{ type: 'image', url: WarningIcon }} />
          <Text fz={14} fw={600}>
            {t('please_ensure_fi_device_returned')}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (isCanceled) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#FFEAA1">
        <Group noWrap>
          <Image image={{ type: 'image', url: WarningIcon }} />{' '}
          <Text fz={14} fw={600}>
            {t('youll_lose_access_to_pet_name_data_when_your_membership_ends', { petName })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (pendingChange) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#F3F3F2">
        <Group noWrap>
          <Image image={{ type: 'image', url: InfoIcon }} />
          <Text fz={14} fw={600}>
            {t('renews_as_price_term', {
              price: formattedCentsToDollars(pendingChange.priceInCents),
              term: pendingChange.term,
            })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (isDiscounted) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#E1FAEE">
        <Group noWrap>
          <Image image={{ type: 'image', url: CheckIcon }} />{' '}
          <Text fz={14} fw={600}>
            {t('discount_applied_renewal_price_at_next_renewal', { renewalPrice: renewalPriceString })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (willPause) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#F3F3F2">
        <Group noWrap>
          <Image image={{ type: 'image', url: InfoIcon }} />
          <Text fz={14} fw={600}>
            {t('your_membership_will_pause_on_date', { date: renewalDateString })}
          </Text>
        </Group>
      </Card.Section>
    );
  }
  if (isCurrentlyPaused) {
    return (
      <Card.Section py={12} px={16} mx={0} bg="#F3F3F2">
        <Group noWrap>
          <Image image={{ type: 'image', url: InfoIcon }} />
          <Text fz={14} fw={600}>
            {t('your_membership_is_paused_until_date', { date: renewalDateString })}
          </Text>
        </Group>
      </Card.Section>
    );
  }

  return <></>;
}
